import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import {
  Play, CheckCircle2, XCircle, RefreshCw, ChevronRight, ChevronDown,
  Code, BookOpen, Zap, Lightbulb, ArrowLeft, Send, Eye, EyeOff
} from 'lucide-react';

const DIFF_COLOR = { beginner: 'bg-emerald-100 text-emerald-700', intermediate: 'bg-yellow-100 text-yellow-700', advanced: 'bg-red-100 text-red-700' };

export default function InteractiveLesson({ lessonId, onBack }) {
  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentSection, setCurrentSection] = useState(0);
  const [exerciseCode, setExerciseCode] = useState({});
  const [runResults, setRunResults] = useState({});
  const [runningIdx, setRunningIdx] = useState(null);
  const [showHints, setShowHints] = useState({});
  const [progress, setProgress] = useState({});
  const [exampleOutputs, setExampleOutputs] = useState({});
  const [runningExample, setRunningExample] = useState(null);

  const fetchLesson = useCallback(async () => {
    try {
      const { data } = await api.get(`/api/train/lessons/interactive/${lessonId}`);
      setLesson(data);
      // Init exercise code from starters
      const codes = {};
      (data.sections || []).forEach((s, i) => {
        if (s.type === 'exercise' && s.starter_code) {
          codes[i] = s.starter_code;
        }
      });
      setExerciseCode(codes);
    } catch { toast.error('Failed to load lesson'); }
    setLoading(false);
  }, [lessonId]);

  const fetchProgress = useCallback(async () => {
    try {
      const { data } = await api.get(`/api/train/lessons/interactive/${lessonId}/progress`);
      const prog = {};
      (data.progress || []).forEach(p => { prog[p.exercise_index] = p; });
      setProgress(prog);
    } catch (e) { /* handled */ }
  }, [lessonId]);

  useEffect(() => { fetchLesson(); fetchProgress(); }, [fetchLesson, fetchProgress]);

  const runExample = async (code, idx) => {
    setRunningExample(idx);
    try {
      const { data } = await api.post('/api/train/run-code', { code, language: 'python' });
      setExampleOutputs(prev => ({ ...prev, [idx]: data }));
    } catch { toast.error('Run failed'); }
    setRunningExample(null);
  };

  const submitExercise = async (sectionIdx, code, expectedOutput) => {
    setRunningIdx(sectionIdx);
    try {
      const { data } = await api.post(`/api/train/lessons/interactive/${lessonId}/submit`, {
        code, exercise_index: sectionIdx, expected_output: expectedOutput,
      });
      setRunResults(prev => ({ ...prev, [sectionIdx]: data }));
      if (data.passed) {
        toast.success('Correct! Exercise passed!');
        fetchProgress();
      } else if (data.execution_success) {
        toast.error('Output doesn\'t match expected. Check your code.');
      } else {
        toast.error('Code has errors. Check the output.');
      }
    } catch { toast.error('Submission failed'); }
    setRunningIdx(null);
  };

  const resetExercise = (idx, starterCode) => {
    setExerciseCode(prev => ({ ...prev, [idx]: starterCode }));
    setRunResults(prev => { const n = { ...prev }; delete n[idx]; return n; });
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <RefreshCw className="w-5 h-5 animate-spin text-gray-400" />
    </div>
  );

  if (!lesson) return <div className="text-center py-20 text-gray-400">Lesson not found</div>;

  const sections = lesson.sections || [];
  const exercises = sections.filter(s => s.type === 'exercise');
  const completedCount = Object.values(progress).filter(p => p.passed).length;

  return (
    <div className="space-y-6" data-testid="interactive-lesson">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <button onClick={onBack} className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 mb-1" data-testid="lesson-back">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to lessons
          </button>
          <h2 className="text-xl font-bold text-gray-900">{lesson.title}</h2>
          <div className="flex items-center gap-2 mt-1">
            <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${DIFF_COLOR[lesson.difficulty] || ''}`}>{lesson.difficulty}</span>
            <span className="text-[10px] text-gray-400">{lesson.language}</span>
            <span className="text-[10px] text-gray-400">{sections.length} sections</span>
            <span className="text-[10px] text-emerald-600">{completedCount}/{exercises.length} exercises completed</span>
            {lesson.ai_generated && <span className="text-[9px] bg-[#D4A843]/10 text-[#D4A843] px-1.5 py-0.5 rounded">AI Generated</span>}
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="w-full bg-gray-200 rounded-full h-1.5">
        <div className="bg-[#D4A843] h-1.5 rounded-full transition-all" style={{ width: `${exercises.length > 0 ? (completedCount / exercises.length) * 100 : 0}%` }} />
      </div>

      {/* Section Navigation Pills */}
      <div className="flex gap-1 flex-wrap">
        {sections.map((s, i) => {
          const isExercise = s.type === 'exercise';
          const exerciseIdx = isExercise ? i : -1;
          const isPassed = progress[exerciseIdx]?.passed;
          return (
            <button
              key={`item-${i}`}
              onClick={() => setCurrentSection(i)}
              className={`px-3 py-1.5 rounded-lg text-[10px] font-medium transition-all border ${
                currentSection === i
                  ? 'bg-[#D4A843] text-[#0A0A0A] border-[#D4A843]'
                  : isPassed
                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                  : 'bg-white text-gray-500 border-gray-200 hover:border-[#D4A843]'
              }`}
              data-testid={`section-nav-${i}`}
            >
              {s.type === 'concept' && <BookOpen className="w-3 h-3 inline mr-1" />}
              {s.type === 'code_example' && <Code className="w-3 h-3 inline mr-1" />}
              {s.type === 'exercise' && (isPassed ? <CheckCircle2 className="w-3 h-3 inline mr-1" /> : <Zap className="w-3 h-3 inline mr-1" />)}
              {i + 1}
            </button>
          );
        })}
      </div>

      {/* Current Section Content */}
      {sections[currentSection] && (() => {
        const section = sections[currentSection];
        const idx = currentSection;

        // ─── CONCEPT SECTION ───
        if (section.type === 'concept') {
          return (
            <div className="bg-white border border-gray-200 rounded-lg p-6" data-testid={`section-concept-${idx}`}>
              <div className="flex items-center gap-2 mb-4">
                <BookOpen className="w-4 h-4 text-[#D4A843]" />
                <h3 className="text-sm font-bold text-gray-900">{section.title}</h3>
              </div>
              <div className="prose prose-sm max-w-none">
                {(section.content || '').split('\n\n').map((p, pi) => (
                  <p key={`p-${pi}`} className="text-[13px] text-gray-700 leading-relaxed mb-3">
                    {p.split(/(`[^`]+`)/).map((part, j) =>
                      part.startsWith('`') && part.endsWith('`')
                        ? <code key={`j-${j}`} className="bg-gray-100 text-[#B91C1C] px-1.5 py-0.5 rounded text-[12px] font-mono">{part.slice(1, -1)}</code>
                        : part.split(/(\*\*[^*]+\*\*)/).map((sub, k) =>
                            sub.startsWith('**') && sub.endsWith('**')
                              ? <strong key={`md-strong-${k}-${sub.slice(0, 16)}`} className="text-gray-900">{sub.slice(2, -2)}</strong>
                              : sub
                          )
                    )}
                  </p>
                ))}
              </div>
              <div className="flex justify-end mt-4">
                <Button size="sm" variant="outline" className="text-xs rounded-lg" onClick={() => setCurrentSection(idx + 1)} data-testid="next-section">
                  Next <ChevronRight className="w-3.5 h-3.5 ml-1" />
                </Button>
              </div>
            </div>
          );
        }

        // ─── CODE EXAMPLE SECTION ───
        if (section.type === 'code_example') {
          const output = exampleOutputs[idx];
          return (
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid={`section-example-${idx}`}>
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <div className="flex items-center gap-2">
                  <Code className="w-4 h-4 text-[#7C3AED]" />
                  <h3 className="text-sm font-bold text-gray-900">{section.title}</h3>
                </div>
                <Button size="sm" onClick={() => runExample(section.code, idx)} disabled={runningExample === idx} className="bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] rounded-lg h-7" data-testid={`run-example-${idx}`}>
                  {runningExample === idx ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : <Play className="w-3 h-3 mr-1" />} Run
                </Button>
              </div>
              <pre className="bg-[#0A0A0A] text-emerald-400 p-4 text-[12px] font-mono leading-relaxed overflow-x-auto"><code>{section.code}</code></pre>
              {section.explanation && (
                <p className="px-4 py-2 text-[11px] text-gray-500 bg-gray-50 border-t border-gray-100">{section.explanation}</p>
              )}
              {output && (
                <div className={`border-t ${output.success ? 'bg-[#0A0A0A]' : 'bg-red-950'}`}>
                  <p className="text-[10px] text-gray-400 px-4 pt-2 font-mono">Output:</p>
                  <pre className={`px-4 pb-3 text-[12px] font-mono ${output.success ? 'text-white' : 'text-red-400'}`}>{output.output || output.error}</pre>
                </div>
              )}
              <div className="flex justify-between p-3 bg-gray-50 border-t border-gray-100">
                {idx > 0 && <Button size="sm" variant="ghost" className="text-[10px]" onClick={() => setCurrentSection(idx - 1)}><ArrowLeft className="w-3 h-3 mr-1" /> Previous</Button>}
                <div className="ml-auto"><Button size="sm" variant="outline" className="text-xs rounded-lg" onClick={() => setCurrentSection(idx + 1)}>Next <ChevronRight className="w-3.5 h-3.5 ml-1" /></Button></div>
              </div>
            </div>
          );
        }

        // ─── EXERCISE SECTION ───
        if (section.type === 'exercise') {
          const code = exerciseCode[idx] || section.starter_code || '';
          const result = runResults[idx];
          const isPassed = progress[idx]?.passed || result?.passed;
          const hints = section.hints || [];
          const showingHints = showHints[idx] || false;

          return (
            <div className={`border rounded-lg overflow-hidden ${isPassed ? 'border-emerald-300 bg-emerald-50/30' : 'border-gray-200 bg-white'}`} data-testid={`section-exercise-${idx}`}>
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <div className="flex items-center gap-2">
                  <Zap className={`w-4 h-4 ${isPassed ? 'text-emerald-600' : 'text-[#D4A843]'}`} />
                  <h3 className="text-sm font-bold text-gray-900">{section.title}</h3>
                  {isPassed && <span className="text-[9px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded font-bold">PASSED</span>}
                </div>
                <div className="flex gap-1.5">
                  {hints.length > 0 && (
                    <Button size="sm" variant="ghost" className="text-[10px] h-7" onClick={() => setShowHints(p => ({ ...p, [idx]: !p[idx] }))} data-testid={`hints-toggle-${idx}`}>
                      <Lightbulb className="w-3 h-3 mr-1 text-yellow-500" /> {showingHints ? 'Hide Hints' : 'Hints'}
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" className="text-[10px] h-7 text-gray-400" onClick={() => resetExercise(idx, section.starter_code)} data-testid={`reset-${idx}`}>
                    <RefreshCw className="w-3 h-3 mr-1" /> Reset
                  </Button>
                </div>
              </div>

              {/* Description */}
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
                <p className="text-[12px] text-gray-700 leading-relaxed">{section.description}</p>
                <p className="text-[10px] text-gray-400 mt-1">Expected output: <code className="bg-white px-1.5 py-0.5 rounded text-[#B91C1C] font-mono">{section.expected_output}</code></p>
              </div>

              {/* Hints */}
              {showingHints && hints.length > 0 && (
                <div className="px-4 py-2 bg-yellow-50 border-b border-yellow-100">
                  {hints.map((h, hi) => (
                    <p key={`h-${hi}`} className="text-[11px] text-yellow-700 flex items-start gap-1.5 mb-1">
                      <Lightbulb className="w-3 h-3 mt-0.5 shrink-0 text-yellow-500" /> {h}
                    </p>
                  ))}
                </div>
              )}

              {/* Code Editor */}
              <div className="relative">
                <textarea
                  value={code}
                  onChange={e => setExerciseCode(prev => ({ ...prev, [idx]: e.target.value }))}
                  className="w-full bg-[#0A0A0A] text-emerald-400 p-4 font-mono text-[12px] leading-relaxed min-h-[180px] resize-y focus:outline-none"
                  spellCheck={false}
                  data-testid={`code-editor-${idx}`}
                />
              </div>

              {/* Result */}
              {result && (
                <div className={`border-t ${result.passed ? 'bg-emerald-950' : result.execution_success ? 'bg-yellow-950' : 'bg-red-950'}`}>
                  <div className="flex items-center gap-2 px-4 pt-2">
                    {result.passed ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <XCircle className="w-4 h-4 text-red-400" />}
                    <span className={`text-xs font-bold ${result.passed ? 'text-emerald-400' : 'text-red-400'}`}>
                      {result.passed ? 'Correct!' : result.execution_success ? 'Wrong Output' : 'Error'}
                    </span>
                    <span className="text-[10px] text-gray-400">Score: {result.score}/100</span>
                  </div>
                  {result.actual_output && (
                    <div className="px-4 py-1">
                      <p className="text-[10px] text-gray-400 font-mono">Your output:</p>
                      <pre className="text-[12px] text-white font-mono">{result.actual_output}</pre>
                    </div>
                  )}
                  {result.error && (
                    <div className="px-4 py-1">
                      <p className="text-[10px] text-gray-400 font-mono">Error:</p>
                      <pre className="text-[12px] text-red-400 font-mono">{result.error}</pre>
                    </div>
                  )}
                  {!result.passed && result.expected_output && (
                    <div className="px-4 pb-2">
                      <p className="text-[10px] text-gray-400 font-mono">Expected:</p>
                      <pre className="text-[12px] text-emerald-400 font-mono">{result.expected_output}</pre>
                    </div>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center justify-between p-3 bg-gray-50 border-t border-gray-100">
                <div className="flex gap-2">
                  {idx > 0 && <Button size="sm" variant="ghost" className="text-[10px]" onClick={() => setCurrentSection(idx - 1)}><ArrowLeft className="w-3 h-3 mr-1" /> Previous</Button>}
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => submitExercise(idx, code, section.expected_output)}
                    disabled={runningIdx === idx}
                    className={`text-xs rounded-lg ${isPassed ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-[#D4A843] hover:bg-[#E8C972]'} ${isPassed ? 'text-white' : 'text-[#0A0A0A]'} font-semibold`}
                    data-testid={`submit-${idx}`}
                  >
                    {runningIdx === idx ? <RefreshCw className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />}
                    Submit & Grade
                  </Button>
                  {idx < sections.length - 1 && (
                    <Button size="sm" variant="outline" className="text-xs rounded-lg" onClick={() => setCurrentSection(idx + 1)}>
                      Next <ChevronRight className="w-3.5 h-3.5 ml-1" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          );
        }

        return null;
      })()}
    </div>
  );
}
